import { extractVariableLabels, makeVariablePattern, normalizeVariableLabel } from '~/components/AgreementStudioPage/TemplateVariables/TemplateVariables.utils';
export const SUBJECT_MAX_LENGTH = 100;
export const MESSAGE_MAX_LENGTH = 400;
const HTML_TAG_PATTERN = /<\/?[a-zA-Z][\s\S]*>/;
function containsHtmlTag(text) {
    return HTML_TAG_PATTERN.test(text);
}
export function effectiveContentLength(text) {
    return [
        ...text.replace(makeVariablePattern(), 'x')
    ].length;
}
export function collectUnknownTemplateVariables(subject, message, allowedSystemKeys) {
    const allowedNormalized = new Set();
    allowedSystemKeys.forEach((key)=>allowedNormalized.add(normalizeVariableLabel(key)));
    const seen = new Set();
    const unknown = [];
    for (const name of [
        ...extractVariableLabels(subject),
        ...extractVariableLabels(message)
    ]){
        const normalized = normalizeVariableLabel(name);
        if (allowedNormalized.has(normalized) || seen.has(normalized)) continue;
        seen.add(normalized);
        unknown.push(name);
    }
    return unknown;
}
export function validateDraft(subject, message, allowedSystemKeys) {
    let options = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : {};
    const errors = {};
    if (!options.skipSubject) {
        const trimmedSubject = subject.trim();
        if (trimmedSubject.length === 0) {
            errors.subject = 'NOTIFICATION_V2_SUBJECT_EMPTY';
        } else if (/[\n\r]/.test(subject)) {
            errors.subject = 'NOTIFICATION_V2_SUBJECT_NEWLINE';
        } else if (containsHtmlTag(trimmedSubject)) {
            errors.subject = 'NOTIFICATION_V2_SUBJECT_RICH_TEXT';
        } else if (effectiveContentLength(trimmedSubject) > SUBJECT_MAX_LENGTH) {
            errors.subject = 'NOTIFICATION_V2_SUBJECT_TOO_LONG';
        }
    }
    const trimmedMessage = message.trim();
    if (trimmedMessage.length === 0) {
        errors.message = 'NOTIFICATION_V2_MESSAGE_EMPTY';
    } else if (containsHtmlTag(trimmedMessage)) {
        errors.message = 'NOTIFICATION_V2_MESSAGE_RICH_TEXT';
    } else if (effectiveContentLength(trimmedMessage) > MESSAGE_MAX_LENGTH) {
        errors.message = 'NOTIFICATION_V2_MESSAGE_TOO_LONG';
    }
    if (allowedSystemKeys != null) {
        const unknown = collectUnknownTemplateVariables(subject, message, allowedSystemKeys);
        if (unknown.length > 0) errors.unknownTemplateVariables = unknown;
    }
    return errors;
}
export function isDraftValid(errors) {
    return errors.subject == null && errors.message == null && (errors.unknownTemplateVariables == null || errors.unknownTemplateVariables.length === 0);
}
export const resolveErrorText = (errors, code)=>{
    if (code == null) return undefined;
    switch(code){
        case 'NOTIFICATION_V2_SUBJECT_EMPTY':
            return errors.NOTIFICATION_V2_SUBJECT_EMPTY();
        case 'NOTIFICATION_V2_SUBJECT_NEWLINE':
            return errors.NOTIFICATION_V2_SUBJECT_NEWLINE();
        case 'NOTIFICATION_V2_SUBJECT_RICH_TEXT':
            return errors.NOTIFICATION_V2_SUBJECT_RICH_TEXT();
        case 'NOTIFICATION_V2_SUBJECT_TOO_LONG':
            return errors.NOTIFICATION_V2_SUBJECT_TOO_LONG({
                limit: SUBJECT_MAX_LENGTH
            });
        case 'NOTIFICATION_V2_MESSAGE_EMPTY':
            return errors.NOTIFICATION_V2_MESSAGE_EMPTY();
        case 'NOTIFICATION_V2_MESSAGE_RICH_TEXT':
            return errors.NOTIFICATION_V2_MESSAGE_RICH_TEXT();
        case 'NOTIFICATION_V2_MESSAGE_TOO_LONG':
            return errors.NOTIFICATION_V2_MESSAGE_TOO_LONG({
                limit: MESSAGE_MAX_LENGTH
            });
        case 'NOTIFICATION_V2_RECIPIENTS_EMPTY':
            return errors.NOTIFICATION_V2_RECIPIENTS_EMPTY();
        case 'NOTIFICATION_V2_CONTENT_NOT_EDITABLE':
            return errors.NOTIFICATION_V2_CONTENT_NOT_EDITABLE();
        case 'NOTIFICATION_V2_RECIPIENTS_NOT_EDITABLE':
            return errors.NOTIFICATION_V2_RECIPIENTS_NOT_EDITABLE();
        case 'NOTIFICATION_V2_MISSING_REQUIRED_TAG':
            return errors.NOTIFICATION_V2_MISSING_REQUIRED_TAG();
        default:
            return errors.default();
    }
};
export const EMPTY_VARIANT = {
    subject: '',
    message: '',
    ctaButtonText: '',
    templateVariables: {}
};
export const UNKNOWN_TEMPLATE_VARIABLES_PREFIX = 'NOTIFICATION_V2_UNKNOWN_TEMPLATE_VARIABLES:';
// WHY: JSON-encoded so template-variable labels containing commas survive the round-trip through react-hook-form's single-string `root.message`.
export const decodeUnknownTemplateVariables = (rootMessage)=>{
    if (rootMessage == null || !rootMessage.startsWith(UNKNOWN_TEMPLATE_VARIABLES_PREFIX)) return [];
    try {
        const parsed = JSON.parse(rootMessage.slice(UNKNOWN_TEMPLATE_VARIABLES_PREFIX.length));
        return Array.isArray(parsed) ? parsed : [];
    } catch (unused) {
        return [];
    }
};
const VARIANT_LABEL_OVERRIDES = {
    invoiceIssuedAutoProcess: {
        standard: (t)=>t.byNotification.invoiceIssuedAutoProcess.standard()
    },
    invoiceIssuedRequiresApproval: {
        auto: (t)=>t.byNotification.invoiceIssuedRequiresApproval.auto(),
        manual: (t)=>t.byNotification.invoiceIssuedRequiresApproval.manual()
    },
    paymentFailedCustomer: {
        standard: (t)=>t.byNotification.paymentFailedCustomer.standard()
    },
    payoutDepositedVendor: {
        standard: (t)=>t.byNotification.payoutDepositedVendor.standard(),
        withdrawal: (t)=>t.byNotification.payoutDepositedVendor.withdrawal()
    },
    payoutFailedVendor: {
        standard: (t)=>t.byNotification.payoutFailedVendor.standard(),
        withdrawal: (t)=>t.byNotification.payoutFailedVendor.withdrawal()
    },
    relationshipApprovedCustomer: {
        noAction: (t)=>t.byNotification.relationshipApprovedCustomer.noAction(),
        standard: (t)=>t.byNotification.relationshipApprovedCustomer.standard()
    },
    relationshipUpdatedWithChanges: {
        standard: (t)=>t.byNotification.relationshipUpdatedWithChanges.standard()
    }
};
const VARIANT_LABEL_PATH_PREFIX = 'pages.notificationSettings.contentCustomization.variantLabels.';
const spacedKey = (key)=>{
    const spaced = key.replace(/([A-Z])/g, ' $1').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};
export const resolveVariantLabel = (notificationName, key, variantLabels)=>{
    var _VARIANT_LABEL_OVERRIDES_notificationName;
    const override = (_VARIANT_LABEL_OVERRIDES_notificationName = VARIANT_LABEL_OVERRIDES[notificationName]) === null || _VARIANT_LABEL_OVERRIDES_notificationName === void 0 ? void 0 : _VARIANT_LABEL_OVERRIDES_notificationName[key];
    if (override != null) {
        const overrideValue = override(variantLabels);
        // Leaked path on a declared override → humanize key, never fall back to the generic map (auto/manual exist there with unrelated descriptors).
        return overrideValue.startsWith(VARIANT_LABEL_PATH_PREFIX) ? spacedKey(key) : overrideValue;
    }
    const localized = variantLabels[key];
    if (typeof localized === 'function') {
        const value = localized();
        if (!value.startsWith(VARIANT_LABEL_PATH_PREFIX)) return value;
    }
    return spacedKey(key);
};
export const seedVariants = (source, keys)=>{
    const seeded = {};
    for (const key of keys){
        var _source_key;
        seeded[key] = (_source_key = source[key]) !== null && _source_key !== void 0 ? _source_key : EMPTY_VARIANT;
    }
    return seeded;
};
export const deriveOrderedVariantKeys = (source, labelOf)=>Object.keys(source).sort((a, b)=>labelOf(a).localeCompare(labelOf(b)));
